'use client';

import Link from 'next/link';
import { useState } from 'react';
import { CheckCircle2, ChevronDown, Dumbbell, ExternalLink, Globe2, List, Share2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';

const faqItems = [
  {
    question: '¿Qué incluye el Plan Gratis?',
    answer: 'Puedes gestionar agenda, clientes y reservas online con 1 profesional, 1 categoría de actividad y hasta 3 servicios. No es una prueba temporal.',
  },
  {
    question: '¿Tengo que instalar algo?',
    answer: 'No. OficioYa funciona desde el navegador en tu celular o computador.',
  },
  {
    question: '¿Puedo usarlo aunque trabaje solo?',
    answer: 'Sí. Está diseñado tanto para profesionales independientes como para pequeños equipos.',
  },
  {
    question: '¿Tengo que usar WhatsApp para usar OficioYa?',
    answer: 'No. Puedes usar el Plan Gratis y recibir reservas desde tu perfil público sin activar el bot de WhatsApp.',
  },
  {
    question: '¿Qué añade el Plan Profesional?',
    answer: 'Por $29.900 COP al mes incluye 1 profesional, más categorías y servicios, recordatorios por WhatsApp desde la agenda y un bot opcional. Cada profesional adicional cuesta $19.900 COP al mes.',
  },
];

export function LandingFaq() {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <div className="border-t border-border bg-bg-primary/70 p-4 sm:p-6 lg:p-8">
      <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.08em] text-accent">Preguntas frecuentes</p>
          <h3 className="mt-1 font-heading text-2xl font-semibold text-text-primary">Lo básico antes de empezar</h3>
        </div>
        <p className="max-w-md text-sm leading-6 text-text-secondary">
          Respuestas cortas para que pruebes OficioYa con tu negocio real sin adivinar condiciones.
        </p>
      </div>

      <div className="overflow-hidden rounded-[20px] border border-border bg-bg-primary">
        {faqItems.map((item, index) => {
          const isOpen = openIndex === index;
          const panelId = `landing-faq-panel-${index}`;
          const buttonId = `landing-faq-button-${index}`;

          return (
            <div key={item.question} className="border-b border-border last:border-b-0">
              <button
                id={buttonId}
                type="button"
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={() => setOpenIndex(isOpen ? -1 : index)}
                className="grid min-h-16 w-full grid-cols-[1fr_auto] items-center gap-4 px-4 py-4 text-left transition-colors hover:bg-bg-secondary/70 motion-reduce:transition-none sm:px-5"
              >
                <span className="font-heading text-base font-semibold leading-6 text-text-primary sm:text-lg">
                  {item.question}
                </span>
                <ChevronDown
                  size={20}
                  className={cn('text-accent transition-transform motion-reduce:transition-none', isOpen && 'rotate-180')}
                  aria-hidden="true"
                />
              </button>
              <div
                id={panelId}
                role="region"
                aria-labelledby={buttonId}
                hidden={!isOpen}
                className="px-4 pb-5 text-sm leading-7 text-text-secondary sm:px-5 sm:text-base"
              >
                {item.answer}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SetupStep({ number, title, active, children }: { number: string; title: string; active?: boolean; children: React.ReactNode }) {
  return (
    <div className="relative grid min-w-0 grid-cols-[auto_minmax(0,1fr)] items-start gap-3 sm:gap-4">
      <span className="relative z-10 grid h-10 w-10 shrink-0 place-items-center rounded-full border border-border bg-bg-secondary font-serif text-xl text-accent">{number}</span>
      <div className={cn('min-w-0 rounded-[1.25rem] border bg-bg-primary p-4 text-text-primary shadow-[0_18px_45px_rgb(76_55_34/0.07)]', active ? 'border-accent' : 'border-border')}>
        <div className="flex min-w-0 items-start justify-between gap-3"><h3 className="min-w-0 text-lg font-bold leading-snug">{title}</h3>{number === '1' ? <CheckCircle2 size={20} className="shrink-0 text-success" /> : number === '2' ? <List size={20} className="shrink-0 text-accent" /> : <Share2 size={20} className="shrink-0 text-accent" />}</div>
        {children}
      </div>
    </div>
  );
}

export function FinalCtaSection() {
  return (
    <section id="crear-negocio" data-section="final_cta" className="bg-bg-primary px-3 pb-8 pt-5 transition-colors duration-300 sm:px-5 sm:pb-12 sm:pt-8">
      <div className="mx-auto max-w-[90rem] overflow-hidden rounded-[1.75rem] border border-border bg-transparent text-text-primary shadow-mockup transition-colors duration-300 sm:rounded-[2.25rem]">
        <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] items-center gap-10 px-5 py-12 sm:px-9 sm:py-16 lg:grid-cols-[minmax(0,1.06fr)_minmax(0,0.94fr)] lg:px-16 lg:py-20">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">Empieza sin complicarte</p>
            <h2 className="mt-5 max-w-3xl text-wrap font-serif text-[2.7rem] leading-[1.02] tracking-[-0.04em] sm:text-6xl lg:text-[4.5rem]">
              Tu negocio puede quedar listo para recibir reservas hoy.
            </h2>
            <p className="mt-5 max-w-2xl text-base leading-7 text-text-secondary">
              El registro te guía por seis pasos: negocio, actividad, profesionales, servicios,
              horarios y plan. Puedes revisar la información antes de terminar.
            </p>
            <div className="mt-8 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
              <Button size="lg" asChild className="min-h-16 px-8 text-lg">
                <Link href="/registro/" data-cta-location="final_cta" data-cta-text="Crear mi negocio gratis">Crear mi negocio gratis</Link>
              </Button>
              <Link href="/perfiles/" className="inline-flex items-center gap-2 px-3 py-2 text-sm font-semibold text-accent">Explorar perfiles públicos <ExternalLink size={16} /></Link>
            </div>
          </div>

          <div className="relative grid min-w-0 gap-4 before:absolute before:bottom-5 before:left-5 before:top-5 before:border-l before:border-dashed before:border-accent/35">
            <SetupStep number="1" title="Negocio y actividad definidos">
              <div className="mt-4 flex min-w-0 items-center gap-4 rounded-xl border border-border p-3"><div className="aspect-[6/5] w-1/3 max-w-24 shrink-0 rounded-xl bg-[url('/images/landing/business-types/training-studio.png')] bg-cover bg-center" /><span className="min-w-0"><strong className="block break-words">Entrenamiento Norte</strong><small className="text-text-secondary">Datos básicos completados</small></span></div>
            </SetupStep>
            <SetupStep number="2" title="Equipo, servicios y horarios configurados">
              <div className="mt-4 grid gap-2">{[['Valoración inicial', '45 min'], ['Sesión personal', '60 min']].map(([service, time]) => <div key={service} className="grid min-w-0 grid-cols-[auto_minmax(0,1fr)] items-center gap-x-3 gap-y-1 rounded-xl border border-border px-3 py-2 sm:grid-cols-[auto_minmax(0,1fr)_auto]"><span className="row-span-2 grid h-8 w-8 place-items-center rounded-full bg-accent/10 text-accent sm:row-span-1"><Dumbbell size={15} /></span><span className="min-w-0 break-words">{service}</span><small className="col-start-2 text-text-secondary sm:col-auto">{time}</small></div>)}</div>
            </SetupStep>
            <SetupStep number="3" title="Plan elegido y perfil listo para compartir" active>
              <div className="mt-4 flex min-w-0 flex-wrap items-center gap-2 rounded-xl border border-border px-3 py-3 text-xs"><Globe2 size={16} className="shrink-0" /><span className="min-w-0 flex-1 truncate">oficioya.co/perfiles/entrenamiento-norte</span><span className="shrink-0 rounded-lg border border-accent px-3 py-2 font-semibold text-accent">Copiar enlace</span></div>
              <span className="mt-3 inline-flex max-w-full items-center gap-2 rounded-full bg-success/10 px-3 py-1.5 text-xs font-medium text-success"><CheckCircle2 size={15} className="shrink-0" />Tu perfil está publicado</span>
            </SetupStep>
          </div>
        </div>

        <div className="flex items-center justify-center gap-4 px-5 pb-10 text-center text-sm"><span className="hidden h-px w-40 bg-border sm:block" /><Sparkles size={18} className="text-accent" />Completa la configuración guiada y publica tu negocio.<span className="hidden h-px w-40 bg-border sm:block" /></div>
        <LandingFaq />
      </div>
    </section>
  );
}
